/**
 * Street Dice Legends — player manual content.
 *
 * ALL player-facing manual copy lives here, separated from the rendering in
 * ManualOverlay.ts so a future i18n layer can swap the source per locale
 * without touching layout. There is no i18n system in the client today; this
 * module is the first localization-ready content table.
 *
 * To localize later: wrap MANUAL_SECTIONS in a t()/locale lookup and translate
 * the string fields (tab/title/tagline/text/title/caption/note/label/name/
 * pays/on) while keeping the block STRUCTURE identical. Icons, accents, and
 * dice faces are presentation and live in ManualOverlay.ts, not here.
 *
 * Copy rule (CLAUDE.md): no connector dashes in any string in this file.
 * Hyphens inside a token (a dice combo like 4-5-6, "token only" written as two
 * words) are fine; a dash joining two clauses is not.
 */

export type ManualTone = 'gold' | 'cyan' | 'magenta' | 'green' | 'red' | 'purple';

export type ManualSectionId = 'lobby' | 'craps' | 'ceelo' | 'wallet' | 'dice';

/** A single renderable block. The renderer in ManualOverlay maps each to DOM. */
export type ManualBlock =
  /** Larger intro paragraph that opens a section. */
  | { type: 'lead'; text: string }
  /** Body paragraph. */
  | { type: 'p'; text: string }
  /** Sub-section heading. */
  | { type: 'heading'; text: string }
  /** A row of CSS-drawn dice with an optional caption. */
  | { type: 'dice'; faces: number[]; caption?: string }
  /** Colored callout box with optional title. */
  | { type: 'callout'; tone: ManualTone; title?: string; text: string }
  /** Numbered flow of steps. */
  | { type: 'steps'; items: Array<{ title: string; text: string }> }
  /** Plain bullet list. */
  | { type: 'list'; items: string[] }
  /** Cee-lo style ranking: label + optional dice faces + a note, ordered. */
  | { type: 'ranks'; rows: Array<{ label: string; faces?: number[]; note: string; tone?: ManualTone }> }
  /**
   * Bet table. Each row has a name, an optional payout string, an optional
   * `betType` (renders that bet's real in-game chip from the shared sidebet
   * icon registry, matching the game's color and glyph), and EITHER:
   *   - `on`: a single "wins on" line (cee-lo even-money bets, where loss is
   *     just the opposite outcome), OR
   *   - `win` + `lose`: an explicit win/lose pair (craps place bets, where the
   *     two conditions differ and are NOT symmetric, e.g. 6/8 busts on more
   *     numbers than 4/10).
   */
  | {
      type: 'bets';
      rows: Array<{
        name: string;
        on?: string;
        win?: string;
        lose?: string;
        pays?: string;
        betType?: string;
      }>;
    }
  /** Highlighted pro tip line. */
  | { type: 'tip'; text: string };

export interface ManualSection {
  id: ManualSectionId;
  /** Short tab label. */
  tab: string;
  /** Section heading shown at the top of the body. */
  title: string;
  /** One line hook under the title. */
  tagline: string;
  accent: ManualTone;
  blocks: ManualBlock[];
}

export const MANUAL_SECTIONS: ManualSection[] = [
  // ─────────────────────────────────────────────────────────── LOBBY ──
  {
    id: 'lobby',
    tab: 'Lobby',
    title: 'The Block',
    tagline: 'Cash on the concrete, dice in your hand.',
    accent: 'gold',
    blocks: [
      {
        type: 'lead',
        text: 'Welcome to Street Dice Legends. No casino, no pit boss. Just you, your bankroll, and a crew watching every roll.',
      },
      { type: 'heading', text: 'Two ways to play' },
      {
        type: 'callout',
        tone: 'cyan',
        title: 'Street Craps',
        text: 'Two dice. Hit the magic numbers or set a point and chase it down before a 7 ends your run.',
      },
      {
        type: 'callout',
        tone: 'magenta',
        title: 'Cee-lo',
        text: 'Three dice. Land 4-5-6 or trips to bank instantly, dodge 1-2-3, and battle the banker head to head.',
      },
      { type: 'heading', text: 'Your money' },
      { type: 'p', text: 'Two kinds of money ride with you everywhere.' },
      {
        type: 'list',
        items: [
          'Stacks are your cash. This is what you bet, win, and lose at the table. Earn more by playing and winning.',
          'Gold is premium money. Spend it on dice skins in the store, never at the table.',
        ],
      },
      { type: 'tip', text: 'Tap your Stacks or Gold up top to load up at the shop.' },
      { type: 'heading', text: 'Get in a game' },
      {
        type: 'steps',
        items: [
          { title: 'Hit the block', text: 'Tap HIT THE BLOCK to drop into a live table.' },
          { title: 'Take a seat', text: 'Join a room and wait for the round to fill up.' },
          { title: 'Lock your dice', text: 'Equip the dice you want before the action starts.' },
        ],
      },
      { type: 'heading', text: 'Daily come up' },
      {
        type: 'p',
        text: 'The gift up top hands you free Stacks every day. Keep your streak alive and the payout keeps growing.',
      },
      { type: 'tip', text: 'New here? Run the tutorial first. It pays you to learn the ropes.' },
    ],
  },

  // ─────────────────────────────────────────────────────────── CRAPS ──
  {
    id: 'craps',
    tab: 'Craps',
    title: 'Street Craps',
    tagline: 'Two dice. One shooter. The whole table watching.',
    accent: 'cyan',
    blocks: [
      {
        type: 'lead',
        text: 'Street craps is fast and loud. The shooter throws two dice, and every number on the felt means something.',
      },
      { type: 'heading', text: 'The come out roll' },
      {
        type: 'p',
        text: 'Every turn starts with the come out roll. This first throw can win it, lose it, or set your target.',
      },
      {
        type: 'callout',
        tone: 'green',
        title: 'Natural, you win',
        text: 'Roll a 7 or 11 on the come out and you take it down right there.',
      },
      {
        type: 'callout',
        tone: 'red',
        title: 'Craps, you lose',
        text: 'Roll a 2, 3, or 12 and the turn is dead on arrival.',
      },
      {
        type: 'callout',
        tone: 'cyan',
        title: 'Point set',
        text: 'Roll a 4, 5, 6, 8, 9, or 10 and that number becomes your point.',
      },
      { type: 'heading', text: 'Chasing the point' },
      {
        type: 'p',
        text: 'Once a point is set, you keep rolling. Hit your point again before a 7 shows and you win. Roll a 7 first and you seven out. Your turn is over and the dice move on.',
      },
      {
        type: 'callout',
        tone: 'gold',
        title: 'The 7 flips on you',
        text: 'On the come out a 7 is gold. After the point is set, that same 7 is the killer. That twist is the heart of craps.',
      },
      { type: 'heading', text: 'Who shoots' },
      {
        type: 'p',
        text: 'A quick high roll picks the first shooter. Win your turn and you keep the dice. Lose and they pass to the next player in line.',
      },
      { type: 'heading', text: 'Fade the shooter' },
      {
        type: 'p',
        text: 'Not shooting? You can still get money in. The shooter puts up a stack and the rest of the table fades it, betting the shooter loses. Win your side and you double your matched cash.',
      },
      { type: 'heading', text: 'Side bets' },
      {
        type: 'p',
        text: 'Once a point is live, back a number. Your bet stays up roll after roll until it wins or busts. The harder the number is to roll, the bigger it pays.',
      },
      {
        type: 'bets',
        rows: [
          {
            name: 'Point 6 or 8',
            betType: 'point_pair_6_8',
            pays: '6 : 5',
            win: 'A 6 or 8 rolls.',
            lose: 'A 7, 11, or any craps (2, 3, or 12) rolls.',
          },
          {
            name: 'Point 5 or 9',
            betType: 'point_pair_5_9',
            pays: '3 : 2',
            win: 'A 5 or 9 rolls.',
            lose: 'A 7 or 11 rolls.',
          },
          {
            name: 'Point 4 or 10',
            betType: 'point_pair_4_10',
            pays: '2 : 1',
            win: 'A 4 or 10 rolls.',
            lose: 'A 7 or 11 rolls.',
          },
        ],
      },
      {
        type: 'p',
        text: 'The easy numbers pay less because they land more often. 6 and 8 are the most common, so they also bust on the most numbers.',
      },
      { type: 'tip', text: 'Side bets are spice, not the meal. Keep your main bankroll for fading the shooter.' },
    ],
  },

  // ─────────────────────────────────────────────────────────── CEE-LO ──
  {
    id: 'ceelo',
    tab: 'Cee-lo',
    title: 'Cee-lo',
    tagline: 'Three dice. Simple to learn, brutal to master.',
    accent: 'magenta',
    blocks: [
      {
        type: 'lead',
        text: 'You throw all three dice until they land on a combo that counts. Find one of these or scoop them up and roll again.',
      },
      { type: 'heading', text: 'The ladder, top to bottom' },
      {
        type: 'ranks',
        rows: [
          { label: '4-5-6', faces: [4, 5, 6], note: 'Instant win. Top of the table. You take it all.', tone: 'green' },
          { label: 'Trips', faces: [6, 6, 6], note: 'Three of a kind. Triple sixes is the highest, triple ones the lowest.', tone: 'gold' },
          { label: 'A point', faces: [5, 5, 3], note: 'A pair plus one odd die. The odd die is your point, 1 through 6. The higher point wins.', tone: 'cyan' },
          { label: '1-2-3', faces: [1, 2, 3], note: 'Instant loss. The worst luck on the block.', tone: 'red' },
          { label: 'Nothing', faces: [1, 3, 5], note: 'No combo at all. Pick them up and roll again.', tone: 'magenta' },
        ],
      },
      { type: 'heading', text: 'Be the bank' },
      {
        type: 'p',
        text: 'Each round one player holds the bank. A quick high roll picks the first banker. The banker sets the pot and everyone else fades it, betting they can beat the bank.',
      },
      {
        type: 'p',
        text: 'The banker rolls, then each player rolls against that result. Beat the bank and you get paid even money. Lose and the bank takes your fade. A tie pushes, and everyone keeps their cash.',
      },
      {
        type: 'callout',
        tone: 'gold',
        title: 'Steal the bank',
        text: 'Land 4-5-6 or triple sixes as a shooter and you snatch the bank for yourself. Anything less and the banker holds the chair.',
      },
      { type: 'heading', text: 'Side bets' },
      { type: 'p', text: 'Call the action before the dice fly. Every cee-lo side bet pays even money. Here is the chip you tap for each one.' },
      {
        type: 'bets',
        rows: [
          { name: 'Bank Wins', betType: 'bank_wins', on: 'The bank beats the shooter.' },
          { name: 'Bank Loses', betType: 'bank_loses', on: 'The shooter beats the bank.' },
          { name: '4+', betType: 'four_up', on: 'The shooter rolls 4-5-6, trips, or a point of 4, 5, or 6.' },
          { name: '3 or Less', betType: 'three_down', on: 'The shooter rolls 1-2-3 or a point of 1, 2, or 3.' },
        ],
      },
      { type: 'tip', text: 'When you hold the bank, the whole table is gunning for you. Set a pot you can cover.' },
    ],
  },

  // ────────────────────────────────────────────────────────── WALLET ──
  {
    id: 'wallet',
    tab: 'Wallet',
    title: 'The Wallet',
    tagline: 'Real cash, real weight, thrown down on the table.',
    accent: 'green',
    blocks: [
      {
        type: 'lead',
        text: 'Your bankroll lives in the wallet. When betting opens it slides in loaded with bills, ready to hit the felt.',
      },
      { type: 'heading', text: 'Pull your cash' },
      {
        type: 'p',
        text: 'Pull a note to grab it, then drop it where you want your money. Bills come in different sizes, so grab the right notes to hit the amount you want to lay down.',
      },
      { type: 'heading', text: 'Lay it down' },
      {
        type: 'steps',
        items: [
          { title: 'Pick a zone', text: 'Each bet has its own spot on the table. Drop your cash there.' },
          { title: 'Cover the bet', text: 'Stack notes until your bet is set the way you want it.' },
          { title: 'Lock in', text: 'Your cash leaves the wallet the moment it lands on the table.' },
        ],
      },
      {
        type: 'callout',
        tone: 'green',
        title: 'Win and collect',
        text: 'Win the bet and your cash comes back with its match on top. You double your money right there on the table.',
      },
      {
        type: 'callout',
        tone: 'cyan',
        title: 'Unmatched comes home',
        text: 'If nobody covers your bet, that cash slides back into your wallet. You only ever risk what gets matched.',
      },
      { type: 'heading', text: 'Know your limit' },
      {
        type: 'p',
        text: 'The wallet only shows notes you can afford. If a bill is greyed out, your Stacks cannot cover it right now.',
      },
      { type: 'tip', text: 'Win Stacks at the table and at the daily gift. A fat wallet means bigger plays.' },
    ],
  },

  // ──────────────────────────────────────────────────────────── DICE ──
  {
    id: 'dice',
    tab: 'My Dice',
    title: 'My Dice',
    tagline: 'Your dice are your signature. Roll in style.',
    accent: 'purple',
    blocks: [
      {
        type: 'lead',
        text: 'Every die you own is a skin you can show off. Let the table know exactly who is shooting.',
      },
      { type: 'heading', text: 'Equip your set' },
      {
        type: 'p',
        text: 'You carry three dice into a game. Tap and hold a die you own, then drag it onto a slot to lock it in.',
      },
      {
        type: 'p',
        text: 'Two slots are for Street Craps and one is for Cee-lo. Build the loadout you like and it shows up the moment you shoot.',
      },
      { type: 'tip', text: 'Save different loadouts as presets and switch sets between games.' },
      { type: 'heading', text: 'Rarities' },
      { type: 'p', text: 'Dice come in tiers. The higher the tier, the rarer the heat.' },
      {
        type: 'list',
        items: [
          'Common and Uncommon: clean everyday rollers.',
          'Rare and Epic: skins that turn heads at the table.',
          'Legendary and Mythic: serious flex.',
          'Exotic: the rarest heat on the block.',
        ],
      },
      { type: 'heading', text: 'The weekly drop' },
      {
        type: 'p',
        text: 'The Dice Store refreshes every Monday with a fresh rotation. See something you want? Lock it in before the week flips.',
      },
      {
        type: 'p',
        text: 'Unlock most dice with Gold or a single Dice Token. Exotic dice are token only, with no gold shortcut to the top.',
      },
      {
        type: 'callout',
        tone: 'gold',
        title: 'Dice Tokens',
        text: 'Tokens are the rare key. You pick them up from store packs and special offers, and one token unlocks any die in the drop.',
      },
      { type: 'heading', text: 'More ways to score dice' },
      {
        type: 'list',
        items: [
          'Finish the tutorial and pocket starter dice for free.',
          'Grab dice packs from offers and watch them rain down.',
          'Every player starts with a default set already in the bag.',
        ],
      },
      { type: 'tip', text: 'Owned dice never expire. Equip them any time from My Dice.' },
    ],
  },
];
